import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo, useRef } from "react";
import {
  User,
  Clock,
  Search,
  ChevronLeft,
  Calendar,
  Stethoscope,
  Users,
  ChevronDown,
  UserPlus,
  X,
  DollarSign,
} from "lucide-react";
import pageLoading from "../assets/pageLoading.json";

import clsx from "clsx";
import { scrollToTop } from "../utils/scrollToTop";

//slider
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useSessionStorageState } from "../hooks/useSessionStorageState";

import { useIsMobile } from "../hooks/use-mobile";
import {
  AvailableDateDto,
  BandAvailabilityDto,
  ClinicAvailableSlotDto,
  ClinicScheduleStatusDto,
  ClinicsClient,
  DoctorScheduleClinicDto,
  DoctorsClient,
  DoctorWithScheduleDto,
  SpecialtiesClient,
} from "../web-api-client";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import InfoSelectBox from "../components/booking/bookingstep/StepChooseInfo/InfoSelectBox";
import CalendarHeader from "../components/booking/bookingstep/StepChooseTime/CalendarHeader";
import CalendarGrid from "../components/booking/bookingstep/StepChooseTime/CalendarGrid";
import TimeSlotGroup from "../components/booking/bookingstep/StepChooseTime/TimeSlotGroup";
import { getSheetContentMap } from "../components/booking/bookingstep/StepChooseInfo/sheetContentMap";
import { CalendarDate } from "../types/calendarDay";
import { MonthData } from "../types/monthData";
import Lottie from "lottie-react";
import ErrorRetry from "../components/ui/ErrorRetry";
import NonScheduleDialog from "../components/NoScheduleDialog";
import {
  getClinicDefaultimg,
  getDoctorDefaultImg,
} from "../utils/defaultImage";
import { DateData } from "../types/DateData";
import { Icon } from "@iconify/react";
import {
  getDateInfo,
  getWeekdayLabel,
  toIsoDateFromYMD,
} from "../utils/getDayLabel";
import { ImageWithFallback } from "../components/landing/ImageWithFallback";
import { DoctorListDesktop } from "../components/booking/bookingstep/DoctorListDesktop";
import LoadingIndicator from "../components/LoadingIndicator";
import { useLanguage } from "../i18n/LanguageContext";
import { appConfig } from "../appconfig";

interface Props {
  flowType: string;
  stepKey: string;
}

type BottomSheetType = "calendar" | "time" | "doctor" | "clinic";

const Step2ChooseDoctorOrTime = ({ flowType, stepKey }: Props) => {
  const [isMobile] = useIsMobile();
  const { t } = useLanguage();
  const clinicDefaultImg = "images/clinic_img2.jpg";

  const sheetTitleMap: Record<BottomSheetType, string> = {
    calendar: t("chonngaykham"),
    time: t("chongiokham"),
    doctor: t("chonbacsi"),
    clinic: t("chonphongkham"),
  };

  const navigate = useNavigate();
  const isByDoctor = flowType === "byDoctor";
  const [searchParams, setSearchParams] = useSearchParams();

  const type = searchParams.get("type"); //doctor / clinic
  const doctorId = searchParams.get("doctorId");
  const clinicId = searchParams.get("specialty");
  const clinicIdByDoctor = searchParams.get("clinicId");

  const [selectedDoctor, setSelectedDoctor] = useSessionStorageState(
    "selectedDoctor",
    null,
  );
  const [selectedClinic, setSelectedClinic] = useSessionStorageState(
    "selectedClinic",
    null,
  );
  const [selectedDate, setSelectedDate] = useSessionStorageState(
    "selectedDate",
    null,
  );
  const [selectedMonth, setSelectedMonth] = useSessionStorageState(
    "selectedMonth",
    null,
  );
  const [selectedYear, setSelectedYear] = useSessionStorageState(
    "selectedYear",
    null,
  );
  const [selectedTime, setSelectedTime] = useSessionStorageState(
    "selectedTime",
    null,
  );

  const [selectedSlotId, setSelectedSlotId] = useState<number>();

  const isPresent = (v: unknown) =>
    v !== null && v !== undefined && (typeof v !== "string" || v.trim() !== "");

  const isValid = useMemo(() => {
    const base = [selectedTime, selectedDate, selectedMonth, selectedYear];

    if (type === "clinic") return [selectedClinic, ...base].every(isPresent);

    if (type === "doctor") return [selectedDoctor, ...base].every(isPresent);

    return base.every(isPresent);
  }, [
    type,
    selectedDoctor,
    selectedTime,
    selectedClinic,
    selectedDate,
    selectedMonth,
    selectedYear,
  ]);

  const [doctorWithSchedule, setDoctorWithSchedule] =
    useState<DoctorScheduleClinicDto>();
  const [doctors, setDoctors] = useState<DoctorWithScheduleDto[]>([]);
  const [clinics, setClinics] = useState<ClinicAvailableSlotDto[]>([]);
  const [localStep, setLocalStep] = useState(stepKey);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [currentYear, setCurrentYear] = useState(2025);
  const [isDateCollapse, setIsDateCollapse] = useState(false);
  const [isCollapseAble, setIsCollapseAble] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [bottomSheetType, setShowBottomSheetType] = useState<BottomSheetType>();
  const [clinicData, setClinicData] = useState<ClinicScheduleStatusDto[]>([]);
  const [hasError, setHasError] = useState(false);
  const [emptyDataMessage, setEmptyDataMessage] = useState("");
  const [openNoti, setOpenNoti] = useState(false);

  const today = new Date();
  const [allMonths, setAllMonths] = useState<MonthData[]>([]);
  const [calendarDays, setCalendarDays] = useState<CalendarDate[]>([]);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  const availableDatesSource = useMemo(() => {
    if (type === "doctor") {
      return (doctorWithSchedule?.availableClinics ?? []).flatMap(
        (clinic) => clinic.availableDates ?? [],
      );
    }

    if (selectedDoctor) {
      return selectedDoctor.availableDates ?? [];
    }

    return selectedClinic?.availableDates ?? [];
  }, [type, selectedDoctor, selectedClinic, doctorWithSchedule]);

  const monthNames = [
    `${t("thang")} 01`,
    `${t("thang")} 02`,
    `${t("thang")} 03`,
    `${t("thang")} 04`,
    `${t("thang")} 05`,
    `${t("thang")} 06`,
    `${t("thang")} 07`,
    `${t("thang")} 08`,
    `${t("thang")} 09`,
    `${t("thang")} 10`,
    `${t("thang")} 11`,
    `${t("thang")} 12`,
  ];
  const weekDays = [t("cn"), t("t2"), t("t3"), t("t4"), t("t5"), t("t6"), t("t7")];

  const suggestedDates = (availableDatesSource ?? []).slice(0, 10);

  const selectedIso =
    selectedDate && selectedMonth && selectedYear
      ? toIsoDateFromYMD(selectedYear, selectedMonth, selectedDate)
      : null;

  const isSelectedInSuggestions =
    !!selectedIso &&
    suggestedDates.some((d: AvailableDateDto) => d.date === selectedIso);

  const dateSliderRef = useRef<any>(null);
  const dateSliderSettings = {
    infinite: false,
    arrows: false,
    dots: false,
    swipe: false,
    draggable: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
  };

  const navigateNextStep = () => {
    searchParams.set("timeSlot", selectedTime.range ?? "");
    searchParams.set("step", "enterInfo");
    if (selectedSlotId) {
      searchParams.set("blockId", String(selectedSlotId));
    }

    setSearchParams(searchParams);
  };

  // Get date range for selected doctor
  const dateRange = useMemo(() => {
    const dates = availableDatesSource.map(
      (d: AvailableDateDto) => new Date(d.date ?? ""),
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const futureDates = dates.filter((date: Date) => date >= today);
    if (futureDates.length === 0) return { minDate: null, maxDate: null };

    return {
      minDate: new Date(Math.min(...futureDates)),
      maxDate: new Date(Math.max(...futureDates)),
    };
  }, [availableDatesSource]);

  // kiểm tra ngày có chọn được không
  const isDateAvailable = (day: number, month: number, year: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
      day,
    ).padStart(2, "0")}`;
    const checkDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (checkDate < today) return false;

    return availableDatesSource.some(
      (d: AvailableDateDto) => d.date === dateStr,
    );
  };

  // Get available time slots for selected date
  const getAvailableTimeSlots = (): BandAvailabilityDto[] => {
    if (!selectedDate) return [];

    const dateStr = `${currentYear}-${String(selectedMonth).padStart(
      2,
      "0",
    )}-${String(selectedDate).padStart(2, "0")}`;
    const dateData = availableDatesSource.find(
      (d: AvailableDateDto) => d.date === dateStr,
    );

    return dateData ? dateData.bands : [];
  };

  const handleDoctorSelect = (doctor: DoctorWithScheduleDto) => {
    scrollToTop();

    setSelectedDoctor(doctor);
    resetDateSelection();

    if (!isMobile) {
      setLocalStep("chooseTime");
      searchParams.set("doctorId", doctor.id ?? "");
      searchParams.set("step", "chooseTime");

      setLocalStep("chooseTime");
      setSearchParams(searchParams);
    }
  };

  const handleClinicSelect = (clinic: ClinicAvailableSlotDto) => {
    scrollToTop();

    setSelectedClinic(clinic);
    resetDateSelection();

    if (!isMobile) {
      setLocalStep("chooseTime");
      searchParams.set("clinicId", "" + clinic.clinicId);
      searchParams.set("step", "chooseTime");

      setLocalStep("chooseTime");
      setSearchParams(searchParams);
    } else {
      setShowBottomSheet(false);

      setTimeout(() => {
        setShowBottomSheetType("calendar");
        setShowBottomSheet(true);
      }, 300);
    }
  };

  const handleBackToList = () => {
    if (flowType === "byTime") {
      setLocalStep("chooseDoctor");
      setSelectedDoctor(null);
    }

    resetDateSelection();
    navigate(-1);
    // onBack();
  };

  const resetDateSelection = () => {
    setSelectedDate(null);
    setSelectedMonth(null);
    setSelectedYear(null);
    setSelectedTime(null);
  };

  const handleTimeSelect = (time: BandAvailabilityDto) => {
    setSelectedTime(time);
    if (time.doctorSlotId) {
      setSelectedSlotId(time.doctorSlotId);
    }

    if (isMobile) {
      setShowBottomSheet(false);
    }

    // navigateNextStep();

    // handleSelect(time);
    // scrollToTop();
  };

  const applyDateSelection = (day: number, month: number, year: number) => {
    // chú ý: isDateAvailable đang nhận month-1
    if (!isDateAvailable(day, month - 1, year)) return;

    setSelectedDate(day);
    setSelectedMonth(month);
    setSelectedYear(year);
    setSelectedTime(null);

    if (isMobile) {
      setShowBottomSheet(false);
      setTimeout(() => {
        setShowBottomSheetType("time");
        setShowBottomSheet(true);
      }, 300);
    } else {
      setIsDateCollapse(true);
      setIsCollapseAble(true);
    }
  };

  const handleDateSelect = (day: CalendarDate) => {
    applyDateSelection(day.day, day.month, day.year);
  };

  const canNavigateToMonth = (month: number, year: number) => {
    if (!dateRange.maxDate) return false;

    const targetDate = new Date(year, month - 1, 1);
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const maxMonth = new Date(
      dateRange.maxDate.getFullYear(),
      dateRange.maxDate.getMonth(),
      1,
    );

    return targetDate >= currentMonth && targetDate <= maxMonth;
  };

  const navigateMonth = (direction: string) => {
    if (isMobile) {
      let newMonth = currentMonth;
      let newYear = currentYear;

      if (direction === "next") {
        if (currentMonth === 12) {
          newMonth = 1;
          newYear = currentYear + 1;
        } else {
          newMonth = currentMonth + 1;
        }
      } else {
        if (currentMonth === 1) {
          newMonth = 12;
          newYear = currentYear - 1;
        } else {
          newMonth = currentMonth - 1;
        }
      }

      if (canNavigateToMonth(newMonth, newYear)) {
        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
      }
    } else {
      direction === "prev"
        ? dateSliderRef.current.slickPrev()
        : dateSliderRef.current.slickNext();

      setIsDateCollapse(false);
      setIsCollapseAble(false);

      const timer = setTimeout(() => {
        let newMonth = currentMonth;
        let newYear = currentYear;

        if (direction === "prev") {
          if (currentMonth === 1) {
            newMonth = 12;
            newYear = currentYear - 1;
          } else {
            newMonth = currentMonth - 1;
          }
        } else {
          if (currentMonth === 12) {
            newMonth = 1;
            newYear = currentYear + 1;
          } else {
            newMonth = currentMonth + 1;
          }
        }
        if (canNavigateToMonth(newMonth, newYear)) {
          setCurrentMonth(newMonth);
          setCurrentYear(newYear);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  };

  // tạo danh sách object ngày trong tháng
  const generateMonthData = (year: number, month: number) => {
    const today = new Date();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    const startDayOfWeek = firstDayOfMonth.getDay(); // 0 (CN) đến 6 (T7)
    const days = [];

    // Ngày tháng trước để fill đầu dòng
    if (startDayOfWeek > 0) {
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

      for (let i = startDayOfWeek - 1; i >= 0; i--) {
        const day = daysInPrevMonth - i;
        const date = new Date(prevYear, prevMonth, day);
        days.push({
          date,
          day,
          month: prevMonth + 1,
          year: prevYear,
          isCurrentMonth: false,
          isToday: false,
          isSelectable: false,
        });
      }
    }

    // Ngày tháng hiện tại
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

      const isSelectable = isDateAvailable
        ? isDateAvailable(day, month, year)
        : true;

      days.push({
        date,
        day,
        month: month + 1,
        year,
        isCurrentMonth: true,
        isToday,
        isSelectable,
      });
    }

    // Ngày tháng sau để fill cuối dòng cho đủ bội số 7
    const remainingCells = 7 - (days.length % 7);
    if (remainingCells < 7) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      for (let day = 1; day <= remainingCells; day++) {
        const date = new Date(nextYear, nextMonth, day);
        days.push({
          date,
          day,
          month: nextMonth + 1,
          year: nextYear,
          isCurrentMonth: false,
          isToday: false,
          isSelectable: false,
        });
      }
    }

    return { year, month: month + 1, days };
  };

  const generateWeekData = (inputDate: Date) => {
    const today = new Date();
    const year = inputDate.getFullYear();
    const month = inputDate.getMonth();
    const date = inputDate.getDate();

    // Ngày trong tuần của inputDate (0 = CN, 1 = T2, ..., 6 = T7)
    const dayOfWeek = inputDate.getDay();

    const week = [];

    for (let i = 0; i < 7; i++) {
      // Lùi về Chủ nhật và tăng lên từng ngày
      const current = new Date(year, month, date - dayOfWeek + i);

      const day = current.getDate();
      const currentMonth = current.getMonth();
      const currentYear = current.getFullYear();

      const isCurrentMonth = currentMonth === month;
      const isToday =
        current.getDate() === today.getDate() &&
        current.getMonth() === today.getMonth() &&
        current.getFullYear() === today.getFullYear();

      const isSelectable = isDateAvailable
        ? isDateAvailable(day, currentMonth, currentYear)
        : true;

      week.push({
        date: current,
        day,
        month: currentMonth + 1,
        year: currentYear,
        isCurrentMonth,
        isToday,
        isSelectable,
      });
    }

    return week;
  };

  function ExpandLine() {
    return (
      <div className="relative w-full flex items-center justify-center mt-4">
        {/* Đường thẳng ngang */}
        <div className="h-px bg-green-500 w-full absolute top-1/2 left-0 transform -translate-y-1/2" />

        {/* Icon ở giữa */}
        <button
          className="expand-icon relative z-10 bg-white rounded-full p-2 hover:text-green-500"
          aria-label="Expand"
          onClick={() => {
            setIsDateCollapse(false);
            setIsCollapseAble(false);
          }}
        >
          <ChevronDown size={20} />
        </button>
      </div>
    );
  }

  const availableTimeSlots = getAvailableTimeSlots();

  const morningSlots: BandAvailabilityDto[] = availableTimeSlots.filter(
    (band: BandAvailabilityDto) => {
      if (!band.start) return false;

      const hour = parseInt(band.start.split(":")[0]);
      return hour < 12;
    },
  );

  const afternoonSlots: BandAvailabilityDto[] = availableTimeSlots.filter(
    (band: BandAvailabilityDto) => {
      if (!band.start) return false;

      const hour = parseInt(band.start.split(":")[0]);
      return hour >= 12;
    },
  );

  const getSelectedDateString = () => {
    return `${String(selectedDate).padStart(2, "0")} 
      / ${String(selectedMonth).padStart(2, "0")}
      / ${selectedYear}`;
  };

  const isDateSelected = (day: CalendarDate) => {
    return (
      selectedDate === day.day &&
      selectedMonth === day.month &&
      day.isSelectable
    );
  };

  const isSuggestedDateSelected = (date: string) => {
    const [year, month, day] = date.split("-").map(Number);

    return (
      selectedDate === day && selectedMonth === month && selectedYear === year
    );
  };

  const handleBackdropClick = (e: any) => {
    if (e.target === e.currentTarget) {
      setShowBottomSheet(false);
    }
  };

  // danh sách bác sĩ theo phòng
  const fetchDoctorByClinic = async () => {
    if (!!clinicId) {
      setIsLoading(true);

      const client = new ClinicsClient(appConfig.apiBaseUrl, {
        fetch: fetchWithTimeout(),
      });

      await client
        .getDoctors(Number(clinicId))
        .then((doctors: DoctorWithScheduleDto[]) => {
          setDoctors(doctors);
          console.log("doctors", doctors);

          // var hasSchedule = doctors.some((doctor) =>
          //   (doctor.availableDates ?? []).some((day) => day.date !== null)
          // );

          // if (!hasSchedule) {
          //   setEmptyDataMessage(
          //     "Không tìm thấy bác sĩ trống lịch theo phòng khám bạn chọn"
          //   );
          // }
        })
        .catch((error) => {
          if (error.name === "AbortError") {
            setHasError(true);

            console.error("Máy chủ mất quá lâu để phản hồi");
          } else {
            console.error("Lỗi khi kết nối danh sách bác sĩ theo phòng", error);
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const fetchClinicsData = async () => {
    const client = new SpecialtiesClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });

    await client
      .getClinics(1, true)
      .then((data) => {
        setClinicData(data);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.error(t("maychuquahan"));
        } else {
          console.error("Có lỗi xảy ra khi kết nối.");
        }
      });
  };

  // lich phong kham
  const fetchClinicTimeSlot = async () => {
    if (!clinicId) return;

    setIsLoading(true);

    const client = new ClinicsClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });

    try {
      const clinicData = await client.getAvailableSlots(
        Number.parseInt(clinicId, 10),
      );

      setSelectedClinic(clinicData);

      // if (clinicData.availableDates?.length === 0) {
      //   setEmptyDataMessage("Phòng khám bạn chọn không có lịch trống.");
      // }
    } catch (error: any) {
      if (error.name === "AbortError") {
        setHasError(true);

        console.error(t("maychuquahan"));
      } else {
        console.error("Lỗi khi lấy lịch phòng khám:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDoctorSchedule = async (doctorId: string) => {
    if (!doctorId) return;

    setIsLoading(true);

    const client = new DoctorsClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });

    try {
      const data = await client.getAvailableTimes(doctorId);

      setDoctorWithSchedule(data);
      setClinics(data.availableClinics ?? []);

      if (data.availableClinics?.length === 0) {
        setEmptyDataMessage(t("khongtimthaychuyenkhoa"));
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        setHasError(true);

        console.error(t("maychuquahan"));
      } else {
        if (error === "Bác sĩ không có lịch trống.") {
          setEmptyDataMessage(t("khongtimthaychuyenkhoa"));
        }

        console.error("Lỗi khi lấy lịch khám bác sĩ:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getClinicName = useMemo(() => {
    if (selectedClinic) {
      return selectedClinic.name;
    }
    if (!clinicData) return t("dangtai");

    const clinic = clinicData.find(
      (c) => String(c.clinicId) === String(clinicId),
    );

    return clinic ? (clinic.clinicName ?? "") : "";
  }, [clinicData]);

  const getDoctorName = (): string => {
    if (!doctorWithSchedule) return "";

    return doctorWithSchedule.doctorName ?? t("dangcapnhat");
  };

  useEffect(() => {
    document.title = t("chonthongtinkham");

    fetchClinicsData();

    if (type === "clinic") {
      fetchDoctorByClinic();
      fetchClinicTimeSlot();
    } else if (type === "doctor") {
      fetchDoctorSchedule(doctorId ?? "");
    }

    const handleBeforeUnload = () => {
      sessionStorage.clear();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup khi unmount
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (showBottomSheet) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    // Cleanup nếu component bị unmount
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [showBottomSheet]);

  useEffect(() => {
    // console.log(selectedClinic?.availableDates);
    const data = generateMonthData(currentYear, currentMonth - 1);
    setCalendarDays(data.days);
  }, [currentMonth, currentYear, selectedClinic, selectedDoctor]);

  useEffect(() => {
    setLocalStep(stepKey);
    if (isMobile === undefined) return;

    if (stepKey === "chooseDoctor" && !isMobile) {
      setSelectedDoctor(null);
    } else if (stepKey === "chooseClinic") {
      setSelectedClinic(null);
    }
  }, [stepKey, type]);

  useEffect(() => {
    if (dateSliderRef.current) {
      dateSliderRef.current.slickGoTo(selectedMonthIndex, true);
    }
  }, [selectedMonthIndex]);

  useEffect(() => {
    if (doctors.length === 0) return;

    if (doctorId) {
      const matchedDoctor = doctors.find((doc) => String(doc.id) === doctorId);
      setSelectedDoctor(matchedDoctor || null);
    } else {
      // setSelectedDoctor(null);
      if (!isMobile) {
        resetDateSelection();
      }
    }

    if (!selectedDate) {
      setIsCollapseAble(false);
      setIsDateCollapse(false);
    }
    // setIsDateCollapse(false);
  }, [doctorId, doctors]);

  useEffect(() => {
    if (!doctorWithSchedule) return;

    if (
      doctorId &&
      doctorWithSchedule.doctorId &&
      doctorWithSchedule.doctorId == doctorId
    ) {
      setSelectedDoctor(doctorWithSchedule);
    }
  }, [doctorId, doctorWithSchedule]);

  useEffect(() => {
    if (clinicData.length === 0 || !isByDoctor) return;

    if (clinicId) {
      const matchedClinic = clinicData.find(
        (clinic) => String(clinic.clinicId) === clinicId,
      );
      setSelectedClinic(matchedClinic || null);
    } else {
      // setSelectedDoctor(null);
      if (!isMobile) {
        resetDateSelection();
      }
    }

    if (!selectedDate) {
      setIsCollapseAble(false);
      setIsDateCollapse(false);
    }
    // setIsDateCollapse(false);
  }, [clinicId, clinicData]);

  useEffect(() => {
    if (clinics.length === 0) return;

    if (clinicIdByDoctor) {
      const matchedClinic = clinics.find(
        (clinic) => String(clinic.clinicId) === clinicIdByDoctor,
      );
      setSelectedClinic(matchedClinic || null);
    } else {
      if (!isMobile) {
        resetDateSelection();
      }
    }

    if (!selectedDate) {
      setIsCollapseAble(false);
      setIsDateCollapse(false);
    }
    // setIsDateCollapse(false);
  }, [clinicIdByDoctor, clinics]);

  useEffect(() => {
    // value của tháng là index từ 0..11
    // selectedMonth là giá trị từ 1..12

    const months = [];
    const startYear = today.getFullYear();
    const startMonth = today.getMonth();

    const endYear = dateRange.maxDate?.getFullYear() ?? 0;
    const endMonth = dateRange.maxDate?.getMonth() ?? 0;

    for (let year = startYear; year <= endYear; year++) {
      const monthStart = year === startYear ? startMonth : 0;
      const monthEnd = year === endYear ? endMonth : 11;

      for (let month = monthStart; month <= monthEnd; month++) {
        const monthData = generateMonthData(year, month);

        if (isDateCollapse && month === selectedMonth - 1) {
          monthData.days = generateWeekData(
            new Date(selectedYear, month, selectedDate),
          );
        }

        months.push(monthData);
      }
    }

    setAllMonths(months);

    setCurrentMonth(startMonth + 1);
    setCurrentYear(startYear);
  }, [dateRange.maxDate]);

  useEffect(() => {
    const monthIndex = allMonths.findIndex(
      (m) => m.year === selectedYear && m.month === selectedMonth,
    );

    setSelectedMonthIndex(monthIndex > 0 ? monthIndex : 0);

    if (selectedMonth) setCurrentMonth(selectedMonth);
    if (selectedYear) setCurrentYear(selectedYear);
  }, [selectedMonth, selectedYear, allMonths]);

  useEffect(() => {
    let days: DateData[] = [];

    if (isDateCollapse) {
      days = generateWeekData(
        new Date(currentYear, selectedMonth - 1, selectedDate),
      );
    } else {
      days = generateMonthData(selectedYear, selectedMonth - 1).days;
    }

    const updatedMonths = allMonths.map((month) =>
      month.month === selectedMonth && month.year === selectedYear
        ? { ...month, days: days }
        : month,
    );

    if (updatedMonths.length > 0) {
      setAllMonths(updatedMonths);
    }
  }, [isDateCollapse, selectedDate]);

  useEffect(() => {
    if (selectedMonth && selectedYear) {
      setCurrentMonth(selectedMonth);
      setCurrentYear(selectedYear);
    } else {
      // Set calendar to current month
      const now = new Date();
      setCurrentMonth(now.getMonth() + 1);
      setCurrentYear(now.getFullYear());
    }
  }, [isMobile, showBottomSheet, selectedMonth]);

  useEffect(() => {
    // if (localStep === 'chooseDoctor') return;
    if (type === "clinic" && isByDoctor) {
      if (isMobile && searchParams.get("doctorId")) {
        navigate(-1);

        searchParams.set("step", "chooseDoctor");
        searchParams.delete("doctorId");
        setSearchParams(searchParams);
      } else if (selectedDoctor && !isMobile) {
        if (localStep !== "chooseTime") {
          searchParams.set("step", "chooseTime");
          searchParams.set("doctorId", selectedDoctor.id);
          setSearchParams(searchParams);
        }
      }
    }

    if (type === "doctor") {
      if (isMobile && searchParams.get("clinicId")) {
        navigate(-1);

        searchParams.set("step", "chooseClinic");
        searchParams.delete("clinicId");
        setSearchParams(searchParams);
      }
    }
  }, [isMobile]);

  useEffect(() => {
    setOpenNoti(emptyDataMessage != "");
  }, [emptyDataMessage]);

  // Left Column - Info
  const LeftColumn = () => (
    <div className="bg-white rounded-b-lg rounded-t-lg shadow-sm ">
      {/* Header */}
      <div className="color-primary text-white px-4 py-3 rounded-t-lg">
        <h2 className="text-lg font-semibold cursor-default">
          {t("thongtindangky")}
        </h2>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {type === "clinic" ? (
          <div className="flex items-center space-x-2">
            <Stethoscope className="w-4 h-4 text-gray-500 mr-2" />
            <div>
              <span className="text-gray-600 text-sm">{t("phonglabel")}</span>
              <div className="text-base">
                {selectedClinic?.name ?? selectedClinic?.clinicName}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2 space-y-3">
            <Users className="w-4 h-4 text-gray-500 mr-2" />
            <div>
              <span className="text-gray-600 text-sm">{t("bacsilabel")}</span>
              <div className="font-medium text-base">{getDoctorName()}</div>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <UserPlus className="w-4 h-4 text-gray-500 mr-2" />
          <div>
            <span className="text-gray-600 text-sm">{t("dichvu")}</span>
            <div className="text-base">
              {type === "clinic"
                ? t("khamchuyenkhoa")
                : t("khamsangloctongquat")}
            </div>
          </div>
        </div>

        <div>
          {selectedDoctor && (
            <div className="flex items-center space-x-2 ">
              <Users className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <span className="text-gray-600 text-sm">{t("bacsilabel")}</span>
                <div className="font-medium text-base">
                  {selectedDoctor?.name}
                </div>
              </div>
            </div>
          )}

          {selectedDoctor && (
            <div className="flex items-center space-x-2 mt-2">
              <DollarSign className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <span className="text-gray-600 text-sm">{t("giakham")}</span>
                <div className="font-medium text-base text-green-700">
                  {(selectedDoctor.consultationFee ?? 1600000).toLocaleString("vi-VN")}đ
                </div>
              </div>
            </div>
          )}

          {selectedClinic && type === "doctor" && (
            <div className="flex items-center space-x-2">
              <Stethoscope className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <span className="text-gray-600 text-sm">{t("phonglabel")}</span>
                <div className="font-medium text-base">
                  {selectedClinic?.name}
                </div>
              </div>
            </div>
          )}

          {selectedDate && (
            <div className="flex items-center space-x-2 mt-3">
              <Calendar className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <span className="text-gray-600 text-sm">{t("ngaykham")}</span>
                <div className="text-base">{getSelectedDateString()}</div>
              </div>
            </div>
          )}

          {selectedTime && (
            <div className="flex items-center space-x-2 mt-3">
              <Clock className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <span className="text-gray-600 text-sm">{t("giokhamlabel")}</span>
                <div className="text-base">{selectedTime.range}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const sheetContentMap = getSheetContentMap({
    setShowBottomSheet,
    showBottomSheet,
    navigateMonth,
    currentMonth,
    currentYear,
    canNavigateToMonth,
    monthNames,
    weekDays,
    calendarDays,
    handleDateSelect,
    isDateSelected,
    doctors,
    handleDoctorSelect,
    morningSlots,
    afternoonSlots,
    selectedTime,
    handleTimeSelect,
    clinicDefaultImg,
    clinics,
    handleClinicSelect,
    warning: true,
  });

  const navigateToChooseTime = () => {
    setLocalStep("chooseTime");
    searchParams.set("step", "chooseTime");
    setSearchParams(searchParams);
  };

  const ClinicListDesktop = () => {
    const [searchTerm, setSearchTerm] = useState("");

    const filteredClinics = clinics.filter((clinic) =>
      clinic.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return (
      <div className="right-column">
        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex space-x-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t("timnhanhphongkham")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-1 focus:ring-green-500 focus:border-transparent outline-none"
                style={{
                  outline: "none",
                  paddingBottom: "0.6rem",
                  paddingTop: "0.6rem",
                }}
              />
            </div>
          </div>
        </div>
        <div className="space-y-4 op-0 animate-slide-up-delay-1">
          {filteredClinics.map((clinic) => (
            <div
              key={clinic.clinicId}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-green-300 hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => {
                handleClinicSelect(clinic);
                setShowBottomSheet(false);
              }}
            >
              <div className="flex items-start h-[100px]">
                {/* clinic Image */}
                <div className="md:w-40 flex-shrink-0 relative overflow-hidden">
                  <img
                    src={clinic.imageUrl ?? getClinicDefaultimg()}
                    alt={clinic.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* clinic Info */}
                <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-card-foreground leading-normal line-clamp-2">
                        {clinic.name}
                      </h3>
                    </div>

                    <p
                      className="text-muted-foreground text-sm leading-5 h-10 overflow-hidden"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        lineHeight: "1.25rem",
                      }}
                    >
                      {clinic.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {searchTerm && filteredClinics.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t("khongtimthaybacsi2")}
            </h3>
            <p className="text-gray-600">{t("thutimkiemtukhoa")}</p>
          </div>
        )}
      </div>
    );
  };

  if (isMobile === undefined) return;

  return isMobile ? (
    isLoading ? (
      <div className="h-full p-4 bg-white items-center flex justify-center pb-[15px]">
        <Lottie
          animationData={pageLoading}
          loop
          autoplay
          style={{ width: 120, height: 120 }}
          aria-label="Loading"
        />
      </div>
    ) : hasError ? (
      <div className="h-full p-4 bg-white flex items-center justify-center pb-[15px]">
        <ErrorRetry />
      </div>
    ) : emptyDataMessage ? (
      <div className="h-full p-4 bg-white flex items-center justify-center pb-[15px]">
        <NonScheduleDialog
          open={openNoti}
          onOpenChange={setOpenNoti}
          onBack={() => navigate(-1)}
          backText={t("quaylai")}
          message={emptyDataMessage}
          allowClose={false}
        />
      </div>
    ) : (
      <div className="h-full bg-white content-component">
        <div className="flex p-4 items-center gap-2">
          {/* <Icon
            icon="healthicons:i-schedule-school-date-time-outline"
            className="w-12 h-12 text-green-600"
          /> */}
          <ImageWithFallback src="/icons/calendar.svg" className="w-9 h-9" />

          <span className="md:text-xl text-lg text-green-700 font-medium">
            {t("chonngayvagiokham")}
          </span>
        </div>
        {/* Form */}
        <div className="p-4 pt-0 op-0 animate-slide-up-delay-1">
          {type === "clinic" ? (
            <>
              {/* Chuyên khoa */}
              <div className="flex gap-2 items-start">
                <span className="text-base font-medium text-gray-500 shrink-0">
                  {t("phonglabel")}
                </span>
                <span className="text-base font-medium">{getClinicName}</span>
              </div>
            </>
          ) : (
            <>
              {/* Chọn bác sĩ */}
              <div className="flex gap-2 items-start">
                <span className="text-base font-medium text-gray-500 shrink-0">
                  {t("bacsilabel")}
                </span>
                <span className="text-base font-medium">{getDoctorName()}</span>
              </div>
            </>
          )}

          {/* Chọn ngày */}
          <div className="mt-6 flex justify-between">
            <span className="font-medium text-base text-green-700">
              {t("chonngaykham")}
            </span>

            <button
              type="button"
              onClick={() => {
                setShowBottomSheetType("calendar");
                setShowBottomSheet(true);
              }}
              className="inline-flex items-center text-sm text-green-600 hover:underline"
            >
              <Calendar className="w-4 h-4 mr-1" />
              <span className="underline">{t("xemthemngaykhac")}</span>
            </button>
          </div>

          <div className="w-full mb-6 overflow-x-auto px-3 py-2 no-scrollbar mt-2">
            <div className="flex flex-nowrap gap-3">
              {selectedDate && !isSelectedInSuggestions && (
                <button
                  key={selectedDate}
                  className={
                    "shrink-0 w-20 py-4 rounded-xl border text-center transition-all " +
                    "bg-green-600 text-white border-green-600"
                  }
                >
                  <p className="text-sm font-medium">
                    {getWeekdayLabel(selectedDate, selectedMonth, selectedYear)}
                  </p>
                  <p className="text-base font-semibold">
                    {`${String(selectedDate).padStart(2, "0")} 
                    / ${String(selectedMonth).padStart(2, "0")}`}
                  </p>
                </button>
              )}
              {suggestedDates.map((d: AvailableDateDto) => {
                if (!d.date) return null;

                const [yearStr, monthStr, dayStr] = d.date.split("-");
                const year = Number(yearStr);
                const month = Number(monthStr);
                const day = Number(dayStr);

                const dateObj = new Date(year, month - 1, day);
                const today = new Date();
                const tomorrow = new Date();
                tomorrow.setDate(today.getDate() + 1);

                const isSameDate = (a: Date, b: Date) =>
                  a.getFullYear() === b.getFullYear() &&
                  a.getMonth() === b.getMonth() &&
                  a.getDate() === b.getDate();

                const dateInfo = getDateInfo(d.date);
                const weekdayLabel = isSameDate(dateObj, today)
                  ? t("homnay")
                  : isSameDate(dateObj, tomorrow)
                    ? t("ngaymai")
                    : dateInfo.weekday;

                return (
                  <button
                    key={d.date}
                    className={
                      "shrink-0 w-20 py-4 rounded-xl border text-center transition-all " +
                      (isSuggestedDateSelected(d.date)
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-white text-gray-700 border-gray-300 hover:border-green-500")
                    }
                    onClick={() => {
                      applyDateSelection(day, month, year);
                    }}
                  >
                    <p
                      className={`text-sm font-medium 
                      ${isSameDate(dateObj, today) && !isSuggestedDateSelected(d.date) ? "text-red-500" : ""}`}
                    >
                      {weekdayLabel}
                    </p>
                    <p className="md:text-lg font-semibold">
                      {dateInfo.formattedDate}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
          {/* <InfoSelectBox
            label=""
            icon={<Calendar className="w-4 h-4 text-green-500" />}
            value={selectedDate ? getSelectedDateString() : ""}
            placeholder="Chọn ngày khám"
            onClick={() => {
              setShowBottomSheetType("calendar");
              setShowBottomSheet(true);
            }}
            disabled={
              (!selectedDoctor && isByDoctor) ||
              (!selectedClinic && type === "doctor")
            }
          /> */}

          {/* Chọn giờ */}
          {selectedDate && (
            <InfoSelectBox
              label={t("giokham")}
              icon={<Clock className="w-4 h-4 text-green-500" />}
              value={selectedTime?.range}
              placeholder={t("chongiokham")}
              onClick={() => {
                setShowBottomSheetType("time");
                setShowBottomSheet(true);
              }}
            />
          )}

          {/* Chọn bác sĩ */}
          {type === "clinic" && (
            <div className="mt-4">
              <div className="flex gap-4 items-center">
                <span className="text-base font-medium text-gray-700">
                  {t("bacsikham")}
                </span>
                {selectedDoctor && (
                  <button
                    className="text-red-500 text-sm underline"
                    onClick={() => {
                      setSelectedDoctor(null);
                      resetDateSelection();
                    }}
                  >
                    {t("bochon")}
                  </button>
                )}
              </div>

              <InfoSelectBox
                label=""
                icon={<User className="w-4 h-4 text-green-500" />}
                value={selectedDoctor?.name}
                placeholder={t("chonbacsikbb")}
                onClick={() => {
                  setShowBottomSheetType("doctor");
                  setShowBottomSheet(true);
                }}
                truncate
              />
            </div>
          )}

          {/* Continue Button */}
          <div className="pt-6 mt-4 flex items-center justify-center">
            <button
              onClick={() => {
                navigateNextStep();
                scrollToTop();
              }}
              className={`w-full button-primary text-white py-3 rounded-full max-w-xs font-normal md:text-lg text-base transition-colors,
                ${
                  isValid
                    ? "text-white"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed pointer-events-none"
                }`}
            >
              <span>{t("tieptuc")}</span>
            </button>
          </div>
        </div>

        {/* Bottom Sheet */}
        <div
          className={clsx(
            "fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-end duration-300",
            showBottomSheet
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none",
          )}
          onClick={(e) => handleBackdropClick(e)}
        >
          <div
            className={clsx(
              "bg-white h-[80%] w-full rounded-t-2xl transform transition-transform duration-300",
              showBottomSheet ? "translate-y-0" : "translate-y-full",
            )}
          >
            {bottomSheetType !== undefined && showBottomSheet && (
              <div>
                {/* Header */}
                <div className="flex items-center justify-between p-3 border-b border-gray-200">
                  <button
                    onClick={() => setShowBottomSheet(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                  <h2 className="text-lg font-medium text-gray-900">
                    {sheetTitleMap[bottomSheetType]}
                  </h2>
                  <div className="w-9 h-9"></div>
                </div>

                {/* Body */}
                {sheetContentMap[bottomSheetType]}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  ) : //Desktop
  isLoading ? (
    <LoadingIndicator />
  ) : hasError ? (
    <div className="h-full p-4 bg-white flex items-center justify-center pb-[15px]">
      <ErrorRetry />
    </div>
  ) : emptyDataMessage ? (
    <div className="h-full p-4 bg-white flex items-center justify-center pb-[15px]">
      <NonScheduleDialog
        open={openNoti}
        onOpenChange={setOpenNoti}
        onBack={() => navigate(-1)}
        backText={t("quaylai")}
        message={emptyDataMessage}
        allowClose={false}
      />
    </div>
  ) : (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="order-2 lg:order-1 lg:col-span-1">
          <LeftColumn />
        </div>

        {/* Right Column */}
        <div className="order-1 lg:order-2 lg:col-span-2">
          {localStep === "chooseTime" || doctors.length <= 0 ? (
            // Chọn thời gian
            <div className="bg-white rounded-lg shadow-sm">
              <div className="color-primary text-white px-6 py-3 rounded-t-lg flex items-center space-x-4">
                {(isByDoctor || type === "doctor") && (
                  <button
                    onClick={handleBackToList}
                    className="p-1 hover:bg-emerald-600 rounded transition-colors duration-200"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <h2 className="text-lg font-semibold cursor-default">
                  {t("vulongchonngaykham")}
                </h2>
              </div>

              <div className="right-column relative op-0 animate-slide-up-delay-1">
                <CalendarHeader
                  currentMonth={currentMonth}
                  currentYear={currentYear}
                  monthNames={monthNames}
                  navigateMonth={navigateMonth}
                  canNavigateToMonth={canNavigateToMonth}
                />

                {allMonths.length > 0 && (
                  <CalendarGrid
                    allMonths={allMonths}
                    currentYear={currentYear}
                    dateSliderRef={dateSliderRef}
                    dateSliderSettings={dateSliderSettings}
                    handleDateSelect={handleDateSelect}
                    isDateSelected={isDateSelected}
                  />
                )}

                {isCollapseAble && <ExpandLine />}

                {selectedDate &&
                  availableTimeSlots.length > 0 &&
                  currentMonth === selectedMonth && (
                    <div className="mt-2 op-0 animate-slide-up-delay-1">
                      <TimeSlotGroup
                        title={t("buoisang")}
                        slots={morningSlots}
                        selectedTime={selectedTime}
                        handleTimeSelect={handleTimeSelect}
                      />
                      <TimeSlotGroup
                        title={t("buoichieu")}
                        slots={afternoonSlots}
                        selectedTime={selectedTime}
                        handleTimeSelect={handleTimeSelect}
                      />
                    </div>
                  )}

                {selectedDate &&
                  selectedTime &&
                  currentMonth === selectedMonth && (
                    <button
                      onClick={() => {
                        navigateNextStep();
                        scrollToTop();
                      }}
                      disabled={!isValid}
                      className="op-0 animate-slide-up-delay-1 button-primary px-4 py-2 rounded absolute bottom-3 right-3 text-white transition-colors duration-200 hover:bg-green-600"
                    >
                      {t("tieptuc")}
                    </button>
                  )}

                {selectedDate && availableTimeSlots.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {t("khongcogiokham")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm ">
              {/* Header */}
              <div className="color-primary text-white px-6 py-3 rounded-t-lg">
                <h2 className="text-lg font-semibold cursor-default">
                  {t("vulongchon")} {type === "clinic" ? t("bacsi") : t("phongkham")}
                </h2>
              </div>

              {/* Content */}
              {type === "clinic" ? (
                <DoctorListDesktop
                  doctors={doctors}
                  onSelect={handleDoctorSelect}
                  onSkip={navigateToChooseTime}
                />
              ) : (
                <ClinicListDesktop />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step2ChooseDoctorOrTime;
